/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { LoggedInUser, LoginService } from '@irian-ro/auth-shared-module';
import { StompService } from '@irian-ro/stomp-module';
import { SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { animate, transition, trigger } from '@angular/animations';
import {EnvironmentService} from "@irian-ro/util-environment";
import { MainMenuItemsService } from './main-menu-items.service';
import { SelectItemsResourceService, SelectItemsStompService } from '@well/select-items-shell-api';
import { LocalStorageService } from 'ngx-webstorage';
import { environment } from '../environments/environment';
import localeRo from '@angular/common/locales/ro';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { LoggedInContextService, WellContextService } from '@well/shared-providers';
import { ContextService } from './context.service';
import { WorkflowsStoreApiService } from '@well/iriro-frontend-libs/workflows/api';
import { translationSchema } from '@well/translation';
import { SelectItemsStoreFacade } from '@well/select-items-state';
import { TermsAndConditionsLocalStorageService } from '@well/shared-components';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { loginUrl } from '@well/navigation';
import { Subscription } from 'rxjs';

@Component({
  selector: 'well-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('move', [
      transition('initial=>final', animate('0.3s ease-in-out')),
      transition('final=>initial', animate('0.3s ease-in-out'))
    ])
  ],
  providers: [SelectItemsStompService],
})
export class AppComponent implements OnInit, OnDestroy {
  context = inject(ContextService);

  userContextService = inject(LoggedInContextService);
  loginService = inject(LoginService);
  workflows = inject(WorkflowsStoreApiService);

  title = 'well';

  env = environment;
  items$ = this.mainMenuItemsService.getMenu();
  languages: SelectItem[] | undefined = undefined;
  isWebSocketConnectionInitialized = false;
  defaultLang = 'ro';
  selectedLang: string | undefined = undefined;
  appContext = { reporter: 'notLoggedInUser', urlHistory: ['http://asd.ro', 'http://asd12.ro'] };

  currentState = 'initial';
  currentToken: string | undefined = undefined;

  private unsubscribe: EventEmitter<boolean> = new EventEmitter();

  isPaymentTermOverdue: boolean | null = null;
  loggedInUser: LoggedInUser | null = null;
  accessUnavailableFeatures = false;

  shouldSetLogoRouterLink = true;
  private routerSubscription: Subscription | undefined = undefined;

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private environmentService: EnvironmentService,
    private companyStompService: StompService,
    private mainMenuItemsService: MainMenuItemsService,
    private selectItemsResourceService: SelectItemsResourceService,
    private selectItemsStompService: SelectItemsStompService,
    private localStorageService: LocalStorageService,
    private wellContextService: WellContextService,
    private selectItemsStoreFacade: SelectItemsStoreFacade,
    private termsAndConditionsLocalStorageService: TermsAndConditionsLocalStorageService
  ) {}

  ngOnInit(): void {
    this.accessUnavailableFeatures = this.environmentService.getEnvironmentProperty('accessUnavailableFeatures');
    this.registerLocals();
    this.subscribeToAuthenticationTokenChanges();

    this.checkRouterLink(this.router.url);  // Initial check
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkRouterLink(event.urlAfterRedirects);
      }
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && !this.router.navigated) {
        this.termsAndConditionsLocalStorageService.clear();
      }
    });

    this.context.onLoggedIn()?.subscribe({
      next: (context: any) => {
        const loggedInUser = context?.loggedInUser;
        this.loggedInUser = context?.loggedInUser;

        this.setContextForIssueReporter(loggedInUser);
        this.changeLanguage({ value: loggedInUser?.language });
        this.workflows.fetchAllConfigurations();

        if (!this.isWebSocketConnectionInitialized) {
          this.companyStompService.initializeWebSocketConnection(
            this.environmentService.getEnvironmentProperty('wsEndpointUrl')
          );
          this.isWebSocketConnectionInitialized = true;
        }

        this.loadSelectItems(loggedInUser?.language ? loggedInUser.language : this.defaultLang);
        this.initializeAndSubscribeToSelectItemsUpdates(
          loggedInUser?.language ? loggedInUser.language : this.defaultLang
        );

        this.wellContextService.getLoggedInContext().subscribe((loggedInContext: any) => {
          this.isPaymentTermOverdue = loggedInContext.paymentTermOverdue;
        });
      },
    });

    this.context.onLoggedOut()?.subscribe({
      next: () => {
        this.companyStompService.disconnect();
        this.workflows.clearAllConfigurations();
        this.selectItemsStompService.stompTopicsUnsubscribe();
        this.isWebSocketConnectionInitialized = false;
        this.changeLanguage({ value: this.defaultLang });
        this.isPaymentTermOverdue = null;
        this.loggedInUser = null;
      },
    });

    this.languages = [
      { label: 'English', value: 'en' },
      { label: 'Romana', value: 'ro' }
    ];
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private checkRouterLink(url: string): void {
    this.shouldSetLogoRouterLink = !url.includes('signup/step');
  }

  changeLanguage({
    value
  }: any) {
    this.selectedLang = value || this.defaultLang;
    this.translateService.use(this.selectedLang);
  }

  onActivate() {
    this.currentState = 'final';
  }

  onDeactivate() {
    this.currentState = 'initial';
  }

  private loadSelectItems(locale: string) {
    this.selectItemsResourceService.getSelectItemsByField().subscribe((res: any) => {
      this.selectItemsStoreFacade.setSelectItems(locale, res.data);
    });
  }

  private initializeAndSubscribeToSelectItemsUpdates(locale: string) {
    this.selectItemsStompService.initializeConnectionAndSubscribeTopics(locale);
    this.selectItemsStompService
      .getSelectItemsUpdateObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res: any) => {
        this.selectItemsStoreFacade.setSelectItems(locale, res);
      });
  }

  private subscribeToAuthenticationTokenChanges() {
    this.currentToken = this.localStorageService.retrieve('authenticationToken');
    this.localStorageService
      .observe('authenticationToken')
      .pipe(takeUntil(this.unsubscribe), debounceTime(100))
      .subscribe((res) => {
        if (res) {
          // this.userContextService.getLoggedInContextFromServer().subscribe();
        } else {
          if (!this.router.url.includes(loginUrl)) {
            this.loginService.logout().subscribe();
          }
        }
      });
  }

  private registerLocals() {
    registerLocaleData(localeRo, 'ro');
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeDe, 'de');
  }

  private setContextForIssueReporter(loggedInUser: LoggedInUser) {
    this.appContext['reporter'] = loggedInUser.name;
  }

  protected readonly translationSchema = translationSchema;
}
